import React from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "flowbite-react";
import type {
  SmartTableConfig,
  SmartTableType,
} from "../../../../types/floorplannerTypes";
import {
  SMART_TABLE_DEFAULT_HEIGHT_M,
  SMART_TABLE_MAX_HEIGHT_M,
  SMART_TABLE_MAX_MANUAL_CHAIRS_PER_EDGE,
  SMART_TABLE_MAX_SEAT_SPACING_M,
  SMART_TABLE_MIN_HEIGHT_M,
  SMART_TABLE_MIN_SEAT_SPACING_M,
  SMART_TABLE_MIN_SIZE_M,
  distributeSmartRectangleChairTotal,
  getSmartRectangleChairTotalCapacity,
} from "../../../../features/floorplan3d/symbols/dining/smartTableConfig";
import { resolveSmartTableLayout } from "../../../../features/floorplan3d/symbols/dining/smartTableLayout";
import DimensionField from "../components/DimensionField";
import ComputedValue from "../components/ComputedValue";
import NumberField from "../components/NumberField";
import RotationField from "../components/RotationField";
import MetricsSection from "../components/MetricsSection";
import SubsectionTitle from "../components/SubsectionTitle";
import ToggleRow from "../components/ToggleRow";
import UnitHeader from "../components/UnitHeader";
import {
  degreesToRadians,
  getCommonDegreesValue,
  getCommonValue,
  panelFieldInputClassName,
  panelFieldLabelClassName,
  panelGridFieldClassName,
  panelObjectTitleRowClassName,
  toDisplayLength,
  toWorldFromDisplay,
} from "../propertyPanelUtils";
import type { UpdateGlobalState } from "../types";
import { observer } from "mobx-react-lite";

type SmartTableSectionProps = {
  selectedSmartTables: SmartTableType[];
  displayedSmartTables: SmartTableType[];
  updateGlobalState: UpdateGlobalState;
};

type SelectFieldProps = {
  label: string;
  /** Optional help text shown in a ? tooltip next to the label. */
  hint?: string;
  value: string | undefined;
  wide?: boolean;
  mixedPlaceholder: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
};

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  hint,
  value,
  wide = false,
  mixedPlaceholder,
  options,
  onChange,
}) => (
  <div
    className={`${panelGridFieldClassName} ${wide ? "col-span-2" : ""}`}
  >
    <span className={`${panelFieldLabelClassName} inline-flex items-center gap-1`}>
      <span className="min-w-0 max-w-full">{label}</span>
      {hint && (
        <Tooltip content={hint} placement="top" style="dark">
          <button
            type="button"
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-gray-400 text-[10px] font-medium leading-none text-gray-500 hover:border-gray-600 hover:text-gray-700"
            aria-label={hint}
          >
            ?
          </button>
        </Tooltip>
      )}
    </span>
    <select
      aria-label={label}
      className={`${panelFieldInputClassName} cursor-pointer px-1 text-xs hover:border-gray-400`}
      style={{ fontSize: 11, paddingLeft: 4, paddingRight: 4 }}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
    >
      {value === undefined && <option value="">{mixedPlaceholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const SmartTableSection: React.FC<SmartTableSectionProps> = observer(({
  selectedSmartTables,
  displayedSmartTables,
  updateGlobalState,
}) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");

  const getTitle = () => {
    if (selectedSmartTables.length > 1) {
      return t("propertyPanel.smartTables", { count: selectedSmartTables.length });
    }
    return selectedSmartTables[0]?.smartTable.shape === "round"
      ? t("symbols.smartRoundTable")
      : t("symbols.smartRectangularTable");
  };

  if (displayedSmartTables.length === 0) return null;

  const firstTable = displayedSmartTables[0];
  const firstConfig = firstTable.smartTable;
  const sameShape = selectedSmartTables.every(
    (table) => table.smartTable.shape === firstConfig.shape,
  );
  const updateConfigs = (
    mutate: (config: SmartTableConfig) => SmartTableConfig,
  ) => {
    selectedSmartTables.forEach((table) => {
      updateGlobalState(
        [table],
        "smartTableConfig",
        mutate(table.smartTable),
      );
    });
  };
  const commonConfigValue = <T,>(
    getValue: (config: SmartTableConfig) => T,
  ): T | undefined =>
    getCommonValue(selectedSmartTables, (table) => getValue(table.smartTable));
  const commonMode = commonConfigValue((config) => config.chairs.mode);
  const commonChairModel = commonConfigValue((config) => config.chairs.model);
  const commonSpacing = commonConfigValue((config) =>
    Math.round(toDisplayLength(config.chairs.seatSpacing)),
  );
  const commonHeight = getCommonValue(selectedSmartTables, (table) =>
    Math.round(
      toDisplayLength(
        table.threeD?.height ?? SMART_TABLE_DEFAULT_HEIGHT_M,
      ),
    ),
  );
  const commonTotal = getCommonValue(
    selectedSmartTables,
    (table) => resolveSmartTableLayout(table.smartTable).chairCounts.total,
  );
  /** Manual total shown in the panel; disabled seating displays as 0. */
  const commonManualTotal = commonConfigValue((config) => {
    if (!config.chairs.enabled) return 0;
    return config.shape === "rectangle"
      ? Object.values(config.chairs.manualCounts).reduce(
          (total, count) => total + count,
          0,
        )
      : config.chairs.manualCount;
  });
  const manualTotalMax =
    sameShape && firstConfig.shape === "rectangle"
      ? Math.min(
          ...selectedSmartTables.map((table) =>
            table.smartTable.shape === "rectangle"
              ? getSmartRectangleChairTotalCapacity(
                  table.smartTable.chairs.manualCounts,
                )
              : SMART_TABLE_MAX_MANUAL_CHAIRS_PER_EDGE,
          ),
        )
      : SMART_TABLE_MAX_MANUAL_CHAIRS_PER_EDGE;
  const commonRotation = getCommonDegreesValue(selectedSmartTables);
  const dimensionValue = (
    getValue: (config: SmartTableConfig) => number,
  ): number | "" => {
    const common = commonConfigValue(getValue);
    return common === undefined
      ? ""
      : Math.round(toDisplayLength(common));
  };

  const updateDimension = (
    key: "width" | "length" | "diameter",
    millimeters: string,
  ) => {
    const parsed = parseFloat(millimeters);
    if (!Number.isFinite(parsed)) return;
    updateConfigs((config) => ({
      ...config,
      [key]: toWorldFromDisplay(parsed),
    }));
  };

  return (
    <div data-testid="smart-table-section">
      <div className="border-b border-gray-300" />
      <div className={panelObjectTitleRowClassName}>
        <UnitHeader title={getTitle()} />
      </div>

      <MetricsSection>
      <div className="grid grid-cols-2 items-end gap-2 py-2">
        {sameShape && firstConfig.shape === "rectangle" && (
          <>
            <DimensionField
              label={t("propertyPanel.width")}
              fieldClassName={panelGridFieldClassName}
              min={toDisplayLength(SMART_TABLE_MIN_SIZE_M)}
              value={dimensionValue((config) =>
                config.shape === "rectangle" ? config.width : 0,
              )}
              placeholder={mixed}
              onChange={(value) => updateDimension("width", value)}
            />
            <DimensionField
              label={t("propertyPanel.length")}
              fieldClassName={panelGridFieldClassName}
              min={toDisplayLength(SMART_TABLE_MIN_SIZE_M)}
              value={dimensionValue((config) =>
                config.shape === "rectangle" ? config.length : 0,
              )}
              placeholder={mixed}
              onChange={(value) => updateDimension("length", value)}
            />
          </>
        )}
        {sameShape && firstConfig.shape === "round" && (
          <DimensionField
            label={t("propertyPanel.diameter")}
            fieldClassName={panelGridFieldClassName}
            min={toDisplayLength(SMART_TABLE_MIN_SIZE_M)}
            value={dimensionValue((config) =>
              config.shape === "round" ? config.diameter : 0,
            )}
            placeholder={mixed}
            onChange={(value) => updateDimension("diameter", value)}
          />
        )}
        <DimensionField
          label={t("propertyPanel.height")}
          fieldClassName={panelGridFieldClassName}
          min={toDisplayLength(SMART_TABLE_MIN_HEIGHT_M)}
          max={toDisplayLength(SMART_TABLE_MAX_HEIGHT_M)}
          value={commonHeight ?? ""}
          placeholder={mixed}
          onChange={(value) => {
            const parsed = parseFloat(value);
            if (Number.isFinite(parsed)) {
              updateGlobalState(selectedSmartTables, "smartTableHeight", parsed);
            }
          }}
        />
        <RotationField
          value={commonRotation ?? ""}
          placeholder={mixed}
          onChange={(degrees) =>
            updateGlobalState(
              selectedSmartTables,
              "rotation",
              degreesToRadians(degrees),
            )
          }
        />
      </div>
      </MetricsSection>

      <SubsectionTitle>{t("propertyPanel.seating")}</SubsectionTitle>
      <div className="grid grid-cols-2 items-end gap-2 px-3 py-2">
        <SelectField
          label={t("propertyPanel.mode")}
          hint={t("propertyPanel.smartTableModeHint")}
          value={commonMode}
          mixedPlaceholder={mixed}
          options={[
            { value: "automatic", label: t("propertyPanel.automatic") },
            { value: "manual", label: t("propertyPanel.manual") },
          ]}
          onChange={(mode) =>
            updateConfigs((config) =>
              config.shape === "rectangle"
                ? {
                    ...config,
                    chairs: {
                      ...config.chairs,
                      // Re-enable when returning to seating controls; use 0 chairs to clear.
                      enabled: true,
                      mode: mode === "manual" ? "manual" : "automatic",
                    },
                  }
                : {
                    ...config,
                    chairs: {
                      ...config.chairs,
                      enabled: true,
                      mode: mode === "manual" ? "manual" : "automatic",
                    },
                  },
            )
          }
        />
        <SelectField
          label={t("propertyPanel.chairStyle")}
          value={commonChairModel}
          mixedPlaceholder={mixed}
          options={[
            { value: "chair", label: t("propertyPanel.chair") },
            { value: "armchair", label: t("propertyPanel.armchair") },
          ]}
          onChange={(model) =>
            updateConfigs((config) =>
              config.shape === "rectangle"
                ? {
                    ...config,
                    chairs: {
                      ...config.chairs,
                      model: model === "armchair" ? "armchair" : "chair",
                    },
                  }
                : {
                    ...config,
                    chairs: {
                      ...config.chairs,
                      model: model === "armchair" ? "armchair" : "chair",
                    },
                  },
            )
          }
        />
        <DimensionField
          label={t("propertyPanel.seatSpacing")}
          min={toDisplayLength(SMART_TABLE_MIN_SEAT_SPACING_M)}
          max={toDisplayLength(SMART_TABLE_MAX_SEAT_SPACING_M)}
          value={commonSpacing ?? ""}
          placeholder={mixed}
          onChange={(value) => {
            const parsed = parseFloat(value);
            if (!Number.isFinite(parsed)) return;
            updateConfigs((config) =>
              config.shape === "rectangle"
                ? {
                    ...config,
                    chairs: {
                      ...config.chairs,
                      seatSpacing: toWorldFromDisplay(parsed),
                    },
                  }
                : {
                    ...config,
                    chairs: {
                      ...config.chairs,
                      seatSpacing: toWorldFromDisplay(parsed),
                    },
                  },
            );
          }}
        />
        {commonMode === "manual" ? (
          <NumberField
            label={t("propertyPanel.totalChairs")}
            min={0}
            max={manualTotalMax}
            step={1}
            value={commonManualTotal ?? ""}
            placeholder={mixed}
            onChange={(value) => {
              const parsed = parseInt(value, 10);
              if (!Number.isFinite(parsed)) return;
              const nextTotal = Math.max(Math.round(parsed), 0);
              updateConfigs((config) =>
                config.shape === "rectangle"
                  ? {
                      ...config,
                      chairs: {
                        ...config.chairs,
                        enabled: nextTotal > 0,
                        manualCounts:
                          distributeSmartRectangleChairTotal(
                            nextTotal,
                            config.chairs.manualCounts,
                          ),
                      },
                    }
                  : {
                      ...config,
                      chairs: {
                        ...config.chairs,
                        enabled: nextTotal > 0,
                        manualCount: Math.min(
                          nextTotal,
                          SMART_TABLE_MAX_MANUAL_CHAIRS_PER_EDGE,
                        ),
                      },
                    },
              );
            }}
          />
        ) : (
          <ComputedValue
            label={t("propertyPanel.totalChairs")}
            value={commonTotal ?? ""}
            placeholder={mixed}
          />
        )}
      </div>

      {sameShape && firstConfig.shape === "rectangle" && commonMode === "automatic" && (
        <div className="px-3 pt-2">
          <ToggleRow
            label={t("propertyPanel.startChair")}
            checked={
              commonConfigValue((config) =>
                config.shape === "rectangle"
                  ? config.chairs.automaticEnds.start
                  : false,
              ) ?? false
            }
            indeterminate={
              commonConfigValue((config) =>
                config.shape === "rectangle"
                  ? config.chairs.automaticEnds.start
                  : false,
              ) === undefined
            }
            onChange={(start) =>
              updateConfigs((config) =>
                config.shape === "rectangle"
                  ? {
                      ...config,
                      chairs: {
                        ...config.chairs,
                        automaticEnds: {
                          ...config.chairs.automaticEnds,
                          start,
                        },
                      },
                    }
                  : config,
              )
            }
          />
          <ToggleRow
            label={t("propertyPanel.endChair")}
            checked={
              commonConfigValue((config) =>
                config.shape === "rectangle"
                  ? config.chairs.automaticEnds.end
                  : false,
              ) ?? false
            }
            indeterminate={
              commonConfigValue((config) =>
                config.shape === "rectangle"
                  ? config.chairs.automaticEnds.end
                  : false,
              ) === undefined
            }
            onChange={(end) =>
              updateConfigs((config) =>
                config.shape === "rectangle"
                  ? {
                      ...config,
                      chairs: {
                        ...config.chairs,
                        automaticEnds: {
                          ...config.chairs.automaticEnds,
                          end,
                        },
                      },
                    }
                  : config,
              )
            }
          />
        </div>
      )}

      {sameShape && firstConfig.shape === "rectangle" && commonMode === "manual" && (
        <div className="grid grid-cols-2 gap-2 px-3 pb-3">
          {(["left", "right", "start", "end"] as const).map((edge) => (
            <NumberField
              key={edge}
              label={t(`propertyPanel.${edge}`)}
              min={0}
              max={100}
              step={1}
              value={
                commonConfigValue((config) =>
                  config.shape === "rectangle"
                    ? config.chairs.manualCounts[edge]
                    : 0,
                ) ?? ""
              }
              placeholder={mixed}
              onChange={(value) => {
                const parsed = parseInt(value, 10);
                if (!Number.isFinite(parsed)) return;
                updateConfigs((config) =>
                  config.shape === "rectangle"
                    ? {
                        ...config,
                        chairs: {
                          ...config.chairs,
                          manualCounts: {
                            ...config.chairs.manualCounts,
                            [edge]: parsed,
                          },
                        },
                      }
                    : config,
                );
              }}
            />
          ))}
        </div>
      )}

      {sameShape && firstConfig.shape === "rectangle" && (
        <div className="grid grid-cols-2 gap-2 px-3 pb-3">
          <ComputedValue
            label={t("propertyPanel.tableLegs")}
            value={
              getCommonValue(
                selectedSmartTables,
                (table) => resolveSmartTableLayout(table.smartTable).legCount,
              ) ?? ""
            }
            placeholder={mixed}
          />
        </div>
      )}
      {sameShape && firstConfig.shape === "round" && (
        <div className="grid grid-cols-2 gap-2 px-3 pb-3">
          <SelectField
            label={t("propertyPanel.support")}
            wide
            mixedPlaceholder={mixed}
            value={commonConfigValue((config) =>
              config.shape === "round" ? config.support : "pedestal",
            )}
            options={[
              { value: "pedestal", label: t("propertyPanel.centerPedestal") },
              { value: "separateLegs", label: t("propertyPanel.separateLegs") },
            ]}
            onChange={(support) =>
              updateConfigs((config) =>
                config.shape === "round"
                  ? {
                      ...config,
                      support:
                        support === "separateLegs"
                          ? "separateLegs"
                          : "pedestal",
                    }
                  : config,
              )
            }
          />
        </div>
      )}
      <div className="border-b border-gray-300" />
    </div>
  );
});

export default SmartTableSection;
